import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  BadgeCheck, UserPlus, UserCheck, Send, DollarSign, Calendar, Play,
  Crown, Share2, Pencil, Upload, X, Music, Mic2, Film, BookOpen, Headphones,
  Instagram, Twitter, Youtube, Globe, MessagesSquare, MapPin, Star, Image as ImageIcon, FileText, Trash2, Download,
} from "lucide-react";
import BookmarkButton from "../components/BookmarkButton";
import { api, resolveFileUrl, contentToQueue } from "../api";
import { useAuth, usePlayer } from "../contexts";
import { toast } from "sonner";

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
export default function ArtistProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { playList } = usePlayer();

  const [profile, setProfile] = useState(null);
  const [following, setFollowing] = useState(false);
  const [tips, setTips] = useState({ total: 0, count: 0, tips: [], streams: 0, stream_earnings: 0, combined_earnings: 0 });
  const [modal, setModal] = useState(null); // "tip" | "book" | "edit" | "followers" | "following" | null
  const [shareToast, setShareToast] = useState(false);
  const [tipPreset, setTipPreset] = useState(null);

  const load = () => {
    api.get(`/profile/${username}`).then((r) => {
      setProfile(r.data);
      setFollowing(!!r.data.is_following);
      if (r.data.id) {
        api.get(`/tips/artist/${r.data.id}`).then((t) => setTips(t.data));
      }
    });
  };
  useEffect(() => { load(); }, [username]);

  const allTracks = useMemo(() => {
    if (!profile?.is_artist) return [];
    // Mixtapes with a `tracks` array unroll into one queue entry per track.
    // Legacy single-file mixtapes and singles keep their existing behaviour.
    return [
      ...(profile.content?.mixtapes || []).flatMap((c) => contentToQueue(c, "mixtapes")),
      ...(profile.content?.singles || []).flatMap((c) => contentToQueue(c, "singles")),
    ];
  }, [profile]);

  const passActiveSnapshot = useMemo(() => {
    // Snapshot at mount; the badge doesn't need to flip mid-session
    const exp = profile?.culture_pass_expires_at;
    if (!exp) return false;
    return Date.parse(exp) > Date.parse(new Date().toISOString().slice(0, -1) + "Z");
  }, [profile?.culture_pass_expires_at]);

  if (!profile) {
    return <div className="max-w-[1600px] mx-auto p-10 font-mono-label text-neutral-500">Loading profile…</div>;
  }

  const passActive = passActiveSnapshot;
  const isSelf = user?.id === profile.id;

  const handleFollow = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (following) {
      await api.delete(`/users/${profile.id}/follow`);
      setFollowing(false);
      setProfile((p) => ({ ...p, followers_count: Math.max(0, p.followers_count - 1) }));
    } else {
      await api.post(`/users/${profile.id}/follow`);
      setFollowing(true);
      setProfile((p) => ({ ...p, followers_count: p.followers_count + 1 }));
    }
  };

  const handleTip = () => {
    if (!user) {
      localStorage.setItem("hc_next", `/artist/${profile.username}`);
      return navigate("/login");
    }
    setModal("tip");
  };
  const handleBook = () => {
    if (!user) {
      localStorage.setItem("hc_next", `/artist/${profile.username}`);
      return navigate("/login");
    }
    setModal("book");
  };
  const handleMessage = () => {
    if (!user) {
      localStorage.setItem("hc_next", `/messages?to=${profile.username}`);
      return navigate("/login");
    }
    navigate(`/messages?to=${profile.username}`);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareToast(true);
      setTimeout(() => setShareToast(false), 1800);
    } catch {
      setShareToast(true);
      setTimeout(() => setShareToast(false), 1800);
    }
  };

  const initials = (profile.display_name || profile.username || "?")
    .split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="pb-40" data-testid="artist-profile-page">
      {/* HERO */}
      <section className="relative grain-bg border-b border-neutral-800 overflow-hidden">
        {profile.banner ? (
          <div
            className="absolute inset-0 opacity-60"
            style={{
              backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.45) 0%, rgba(5,5,5,0.95) 95%), url('${resolveFileUrl(profile.banner)}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            data-testid="profile-banner"
          />
        ) : (
          <div
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage: profile.avatar ? `url('${resolveFileUrl(profile.avatar)}')` : "",
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(60px)",
            }}
          />
        )}
        <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 py-14 grid md:grid-cols-[220px_1fr] gap-8 items-end">
          {profile.avatar ? (
            <img
              src={resolveFileUrl(profile.avatar)}
              alt={profile.display_name}
              className="w-44 h-44 object-cover border border-neutral-800"
              data-testid="artist-avatar"
            />
          ) : (
            <div className="w-44 h-44 bg-neutral-900 border border-neutral-800 flex items-center justify-center font-display text-6xl text-neutral-500" data-testid="artist-avatar-fallback">
              {initials}
            </div>
          )}
          <div>
            <div className="font-mono-label text-[11px] text-[var(--accent)] mb-3 flex gap-3 items-center flex-wrap">
              {profile.is_artist ? "ARTIST" : "MEMBER"}
              {profile.verified && (
                <span className="inline-flex items-center gap-1"><BadgeCheck size={12} /> VERIFIED</span>
              )}
              {profile.is_indy && (
                <span className="chip chip-accent" data-testid="tag-indy">INDY</span>
              )}
              {passActive && (
                <span className="chip chip-accent inline-flex items-center gap-1"><Crown size={10} /> CULTURE PASS</span>
              )}
            </div>
            <h1 className="font-display text-5xl md:text-7xl uppercase leading-none mb-3" data-testid="artist-display-name">
              {profile.display_name}
            </h1>
            <div className="font-mono-label text-[11px] text-neutral-400 mb-4">@{profile.username}</div>
            {profile.bio && <p className="text-neutral-300 max-w-2xl mb-6">{profile.bio}</p>}

            {profile.is_artist && (
              <div className="flex flex-wrap gap-2 mb-5" data-testid="artist-tags">
                {profile.account_type && <span className="chip" data-testid="tag-account-type">{profile.account_type.toUpperCase()}</span>}
                {profile.service_title && <span className="chip">{profile.service_title.toUpperCase()}</span>}
                {profile.company_name && <span className="chip">{profile.company_name.toUpperCase()}</span>}
                {profile.genre && <span className="chip" data-testid="tag-genre">{profile.genre.toUpperCase()}</span>}
                {(profile.city || profile.state || profile.location) && (
                  <span className="chip inline-flex items-center gap-1" data-testid="tag-location">
                    <MapPin size={10} /> {[profile.city, profile.state].filter(Boolean).join(", ").toUpperCase() || profile.location?.toUpperCase()}
                  </span>
                )}
                {profile.rate_amount > 0 && <span className="chip chip-accent" data-testid="tag-rate">${profile.rate_amount}/{profile.rate_unit || "hr"}</span>}
                {(profile.availability || []).map((a) => (
                  <span key={a} className="chip">{a.replace("_", " ").toUpperCase()}</span>
                ))}
                {profile.experience_level && <span className="chip">{profile.experience_level.toUpperCase()}-LEVEL</span>}
                {profile.open_to_bookings && <span className="chip chip-accent" data-testid="tag-open">OPEN TO BOOKINGS</span>}
              </div>
            )}

            {/* Skills */}
            {profile.skills && profile.skills.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-5" data-testid="artist-skills">
                {profile.skills.map((s) => (
                  <span key={s} className="font-mono-label text-[10px] text-neutral-400 border border-neutral-800 px-2 py-1">{s.toUpperCase()}</span>
                ))}
              </div>
            )}

            {/* Reviews summary */}
            {profile.rating_count > 0 && (
              <div className="font-mono-label text-[11px] text-neutral-400 mb-4 flex items-center gap-2" data-testid="rating-summary">
                <Star size={14} className="text-[var(--accent)] fill-[var(--accent)]" />
                <span className="font-headline text-white">{profile.rating_avg.toFixed(1)}</span> · {profile.rating_count} review{profile.rating_count === 1 ? "" : "s"}
                {profile.booking_count > 0 && <span className="ml-2">· {profile.booking_count} completed bookings</span>}
              </div>
            )}

            {/* Social links */}
            {profile.social_links && Object.keys(profile.social_links).length > 0 && (
              <div className="flex gap-3 items-center mb-6" data-testid="artist-social">
                {Object.entries(profile.social_links).map(([k, v]) => v && (
                  <a key={k} href={v} target="_blank" rel="noreferrer" className="text-neutral-400 hover:text-[var(--accent)]" data-testid={`social-${k}`} aria-label={k}>
                    {socialIcon(k)}
                  </a>
                ))}
              </div>
            )}

            {/* Stats */}
            <div className="flex flex-wrap gap-6 mb-6 font-mono-label text-[11px] text-neutral-400">
              <button onClick={() => setModal("followers")} className="hover:text-white text-left" data-testid="stat-followers">
                <div className="font-headline text-2xl text-white">{profile.followers_count}</div>
                FOLLOWERS
              </button>
              <button onClick={() => setModal("following")} className="hover:text-white text-left" data-testid="stat-following">
                <div className="font-headline text-2xl text-white">{profile.following_count}</div>
                FOLLOWING
              </button>
              {profile.is_artist && (
                <>
                  <button onClick={() => setModal("tip")} className="text-left" data-testid="stat-tipped" title="Tip this artist">
                    <div className="font-headline text-2xl text-[var(--accent)]">${tips.total.toFixed(0)}</div>
                    TIPPED · {tips.count}
                  </button>
                  <div data-testid="stat-stream-earnings" title={`Estimated stream payout @ $${(tips.stream_rate || 0.005).toFixed(3)}/play`}>
                    <div className="font-headline text-2xl text-white">${(tips.stream_earnings || 0).toFixed(2)}</div>
                    STREAM PAYOUT
                  </div>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              {isSelf ? (
                <>
                  <button onClick={() => setModal("edit")} className="btn-primary" data-testid="edit-profile-btn">
                    <Pencil size={14} /> Edit Profile
                  </button>
                  {profile.is_artist && (
                    <Link to="/studio" className="btn-ghost" data-testid="studio-link">
                      <Mic2 size={14} /> Open Studio
                    </Link>
                  )}
                </>
              ) : (
                user && (
                  <button onClick={handleFollow} className={following ? "btn-ghost" : "btn-primary"} data-testid="follow-btn">
                    {following ? <><UserCheck size={14} /> Following</> : <><UserPlus size={14} /> Follow</>}
                  </button>
                )
              )}
              {profile.is_artist && allTracks.length > 0 && (
                <button onClick={() => playList(allTracks)} className="btn-ghost" data-testid="artist-play-all">
                  <Play size={14} /> Play All
                </button>
              )}
              {profile.is_artist && !isSelf && (
                <>
                  <button onClick={handleTip} className="btn-primary" data-testid="tip-btn">
                    <DollarSign size={14} /> Tip Artist
                  </button>
                  <button onClick={handleMessage} className="btn-ghost" data-testid="message-btn">
                    <MessagesSquare size={14} /> Message
                  </button>
                  <button onClick={handleBook} className="btn-ghost" data-testid="book-btn">
                    <Calendar size={14} /> Book Artist
                  </button>
                  <BookmarkButton targetUserId={profile.id} />
                </>
              )}
              <button onClick={handleShare} className="btn-ghost" data-testid="share-btn">
                <Share2 size={14} /> Share
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* SUPPORT STRIP — high-visibility Tip CTA */}
      {profile.is_artist && !isSelf && (
        <section className="max-w-[1600px] mx-auto px-4 sm:px-6 pt-8" data-testid="artist-tip-strip">
          <div className="card-brut p-5 sm:p-6 border-[var(--accent)] bg-gradient-to-r from-[#1a0a05] via-black to-black flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-1">SUPPORT THE CULTURE</div>
              <h3 className="font-display text-2xl sm:text-3xl uppercase">Tip {profile.display_name} directly</h3>
              <p className="font-mono-label text-[10px] text-neutral-400 mt-2">
                100% GOES TO THE ARTIST · CULTURE PASS MEMBERS GET A VERIFIED BADGE
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {[5, 10, 25].map((v) => (
                <button
                  key={v}
                  onClick={() => {
                    if (!user) { localStorage.setItem("hc_next", `/artist/${profile.username}`); return navigate("/login"); }
                    setTipPreset(v);
                    setModal("tip");
                  }}
                  className="btn-ghost"
                  data-testid={`quick-tip-${v}`}
                >
                  ${v}
                </button>
              ))}
              <button onClick={() => { setTipPreset(null); handleTip(); }} className="btn-primary" data-testid="tip-strip-btn">
                <DollarSign size={14} /> Tip Now
              </button>
            </div>
          </div>
        </section>
      )}

      {/* SUPPORTERS */}
      {profile.is_artist && tips.tips?.length > 0 && (
        <section className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12" data-testid="artist-supporters">
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">RECENT SUPPORTERS</div>
          <h2 className="font-headline text-2xl uppercase mb-6">Tips From The Culture</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tips.tips.slice(0, 6).map((t) => (
              <div key={t.id} className="card-brut p-4" data-testid={`supporter-${t.id}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-display text-2xl">${t.amount.toFixed(0)}</span>
                  {t.is_pass_supporter && (
                    <span className="chip chip-accent inline-flex items-center gap-1" data-testid={`supporter-badge-${t.id}`}>
                      <Crown size={10} /> VERIFIED SUPPORTER
                    </span>
                  )}
                </div>
                {t.message && <p className="text-neutral-300 text-sm italic mt-2">&ldquo;{t.message}&rdquo;</p>}
                <div className="font-mono-label text-[10px] text-neutral-500 mt-3">{new Date(t.created_at).toLocaleDateString()}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* DISCOGRAPHY (artist only) */}
      {profile.is_artist && <Discography content={profile.content || {}} />}

      {/* PORTFOLIO */}
      {(profile.portfolio?.length > 0 || isSelf) && (
        <PortfolioSection profile={profile} isSelf={isSelf} onChange={load} />
      )}

      {/* REVIEWS */}
      {(profile.reviews?.length > 0 || isSelf) && (
        <ReviewsSection profile={profile} />
      )}

      {/* VERIFICATION CTA (self only) */}
      {isSelf && !profile.verified && (
        <section className="max-w-[1600px] mx-auto px-4 sm:px-6 pb-8" data-testid="verification-cta">
          <div className="card-brut p-5 sm:p-6 border-neutral-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-1">GET VERIFIED</div>
              <h3 className="font-headline text-xl uppercase">{profile.verification_requested ? "Verification In Review" : "Request Account Verification"}</h3>
              <p className="font-mono-label text-[10px] text-neutral-400 mt-2">
                {profile.verification_requested
                  ? "Our team is reviewing your account. You'll be promoted to Verified Artist and unlock the Studio uploads once approved."
                  : "Verified Artists unlock Studio uploads (music + videos), rank higher in search, win more bookings, and earn a Verified badge."}
              </p>
            </div>
            {!profile.verification_requested && (
              <button
                onClick={async () => { await api.post("/profile/verify-request"); load(); }}
                className="btn-primary"
                data-testid="request-verification-btn"
              >
                <BadgeCheck size={14} /> Request Verification
              </button>
            )}
          </div>
        </section>
      )}

      {/* NON-ARTIST FALLBACK */}
      {!profile.is_artist && (
        <section className="max-w-[1600px] mx-auto px-4 sm:px-6 py-16">
          <div className="border border-dashed border-neutral-800 p-12 text-center">
            <Headphones size={32} className="mx-auto text-neutral-600 mb-4" />
            <p className="font-mono-label text-neutral-500 text-sm">
              {isSelf ? "You haven't enabled artist mode yet. Edit your profile to unlock the artist features." : `${profile.display_name} is just listening for now.`}
            </p>
            {isSelf && (
              <button onClick={() => setModal("edit")} className="btn-primary mt-6" data-testid="enable-artist-btn">
                <Mic2 size={14} /> Enable Artist Mode
              </button>
            )}
          </div>
        </section>
      )}

      {/* Modals */}
      {modal === "tip" && <TipModal artist={profile} preset={tipPreset} onClose={() => { setModal(null); setTipPreset(null); load(); }} />}
      {modal === "book" && <BookModal artist={profile} onClose={() => setModal(null)} />}
      {modal === "edit" && (
        <EditProfileModal
          profile={profile}
          onClose={() => setModal(null)}
          onSaved={(fresh) => {
            setProfile((p) => ({ ...p, ...fresh }));
            setUser(fresh);
            setModal(null);
            // If username didn't change, just refresh tips/etc.
            if (fresh.username && fresh.username !== username) {
              navigate(`/artist/${fresh.username}`);
            } else { load(); }
          }}
        />
      )}
      {modal === "followers" && (
        <UsersListModal title="Followers" endpoint={`/users/${profile.id}/followers`} onClose={() => setModal(null)} testid="followers-modal" />
      )}
      {modal === "following" && (
        <UsersListModal title="Following" endpoint={`/users/${profile.id}/following`} onClose={() => setModal(null)} testid="following-modal" />
      )}

      {shareToast && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 bg-[var(--surface)] border border-[var(--accent)] px-4 py-2 font-mono-label text-[11px] z-50" data-testid="share-toast">
          LINK COPIED TO CLIPBOARD
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Discography w/ tabs
// ─────────────────────────────────────────────────────────────────────────────
const DISCO_TABS = [
  { key: "all", label: "All", icon: Play },
  { key: "mixtapes", label: "Mixtapes", icon: Music },
  { key: "singles", label: "Singles", icon: Mic2 },
  { key: "videos", label: "Videos", icon: Film },
  { key: "books", label: "Books", icon: BookOpen },
];

function Discography({ content }) {
  const [tab, setTab] = useState("all");
  const allItems = [
    ...(content.mixtapes || []).map((x) => ({ ...x, _type: "mixtapes" })),
    ...(content.singles || []).map((x) => ({ ...x, _type: "singles" })),
    ...(content.videos || []).map((x) => ({ ...x, _type: "videos" })),
    ...(content.books || []).map((x) => ({ ...x, _type: "books" })),
  ];
  const items = tab === "all" ? allItems : (content[tab] || []).map((x) => ({ ...x, _type: tab }));
  const totalEverywhere = allItems.length;

  return (
    <section className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12" data-testid="discography">
      <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">DISCOGRAPHY</div>
      <div className="flex items-end justify-between mb-6">
        <h2 className="font-headline text-2xl uppercase">The Body of Work</h2>
      </div>
      <div className="flex gap-1 border-b border-neutral-800 mb-6 overflow-x-auto" data-testid="disco-tabs">
        {DISCO_TABS.map((t) => {
          const count = t.key === "all" ? totalEverywhere : (content[t.key] || []).length;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`font-mono-label text-[11px] px-4 py-3 border-b-2 inline-flex items-center gap-2 transition-colors ${
                tab === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-neutral-400 hover:text-white"
              }`}
              data-testid={`disco-tab-${t.key}`}
            >
              <t.icon size={12} /> {t.label.toUpperCase()} <span className="text-neutral-500">{count}</span>
            </button>
          );
        })}
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-12 text-center" data-testid="disco-empty">
          <p className="font-mono-label text-neutral-500 text-sm">No drops here yet. Follow to hear it first.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((m) => {
            const isDownloadable = m.allow_downloads !== false && !!m.file_url;
            const hasMediaKit = !!m.media_kit_url;
            return (
              <div key={`${m._type}-${m.id}`} className="card-brut block group" data-testid={`disco-${m._type}-${m.id}`}>
                <Link to={`/${m._type}/${m.id}`} className="block">
                  <div className={`overflow-hidden ${m._type === "videos" ? "aspect-video" : m._type === "books" ? "aspect-[2/3]" : "aspect-square"}`}>
                    <img src={resolveFileUrl(m.cover_image)} alt={m.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                  </div>
                  <div className="p-3 border-t border-neutral-800 flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <div className="font-headline text-sm uppercase truncate">{m.title}</div>
                      <div className="font-mono-label text-[10px] text-neutral-400 mt-1 uppercase">{m._type}</div>
                    </div>
                    {m.early_access && <Crown size={12} className="text-[var(--accent)] shrink-0 mt-1" />}
                  </div>
                </Link>
                {(isDownloadable || hasMediaKit) && (
                  <div className="px-3 pb-3 flex flex-wrap gap-2">
                    {isDownloadable && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/content/${m._type}/${m.id}/download`}
                        className="font-mono-label text-[10px] inline-flex items-center gap-1 text-[var(--accent)] hover:underline"
                        data-testid={`disco-download-${m._type}-${m.id}`}
                      >
                        <Download size={11} /> DOWNLOAD
                      </a>
                    )}
                    {hasMediaKit && (
                      <a
                        href={resolveFileUrl(m.media_kit_url)}
                        target="_blank" rel="noreferrer"
                        className="font-mono-label text-[10px] inline-flex items-center gap-1 text-neutral-400 hover:text-white"
                        data-testid={`disco-kit-${m._type}-${m.id}`}
                      >
                        <Download size={11} /> KIT.ZIP
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modals
// ─────────────────────────────────────────────────────────────────────────────
function ModalShell({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[var(--surface)] border border-neutral-800 p-6 max-w-md w-full my-8">
        {children}
      </div>
    </div>
  );
}

function TipModal({ artist, preset, onClose }) {
  const [amount, setAmount] = useState(preset || 5);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/tip", {
        artist_id: artist.id,
        amount: parseFloat(amount),
        message: msg,
        origin_url: window.location.origin,
      });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Tip failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell onClose={onClose} testid="tip-modal">
      <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">TIP THE ARTIST</div>
      <h3 className="font-display text-3xl uppercase mb-6">Support {artist.display_name}</h3>
      <div className="flex gap-2 mb-4">
        {[5, 10, 25, 50, 100].map((v) => (
          <button key={v} onClick={() => setAmount(v)} className={`chip ${amount===v ? "chip-accent" : ""}`} data-testid={`tip-amt-${v}`}>${v}</button>
        ))}
      </div>
      <input type="number" min="1" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-brut mb-3" data-testid="tip-amount-input" />
      <textarea placeholder="Optional message…" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} className="input-brut mb-4 resize-none" data-testid="tip-message" />
      <div className="flex gap-2">
        <button onClick={submit} disabled={busy} className="btn-primary flex-1 justify-center" data-testid="tip-submit"><DollarSign size={14} /> {busy ? "Redirecting…" : `Tip $${amount}`}</button>
        <button onClick={onClose} className="btn-ghost">Cancel</button>
      </div>
    </ModalShell>
  );
}

function BookModal({ artist, onClose }) {
  const [form, setForm] = useState({ event_date: "", event_type: "show", budget: 500, details: "", contact_email: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/bookings", { ...form, artist_id: artist.id, budget: parseFloat(form.budget) });
      setDone(true);
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Booking failed");
    } finally { setBusy(false); }
  };
  return (
    <ModalShell onClose={onClose} testid="book-modal">
      <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">BOOK THE ARTIST</div>
      <h3 className="font-display text-3xl uppercase mb-6">Book {artist.display_name}</h3>
      {done ? (
        <div className="text-center py-6">
          <Send className="mx-auto text-[var(--acid)]" size={36} />
          <p className="font-headline mt-4 uppercase">Request Sent</p>
          <p className="text-neutral-400 text-sm mt-2">{artist.display_name} will reach out via email.</p>
          <button onClick={onClose} className="btn-ghost mt-6">Close</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <input type="date" required value={form.event_date} onChange={(e) => setForm({...form,event_date:e.target.value})} className="input-brut" data-testid="book-date" />
          <select value={form.event_type} onChange={(e) => setForm({...form,event_type:e.target.value})} className="input-brut" data-testid="book-type">
            <option value="show">Live Show</option>
            <option value="feature">Feature/Verse</option>
            <option value="studio">Studio Session</option>
          </select>
          <input type="number" required placeholder="Budget USD" value={form.budget} onChange={(e) => setForm({...form,budget:e.target.value})} className="input-brut" data-testid="book-budget" />
          <input type="email" required placeholder="Contact Email" value={form.contact_email} onChange={(e) => setForm({...form,contact_email:e.target.value})} className="input-brut" data-testid="book-email" />
          <textarea required placeholder="Details" rows={3} value={form.details} onChange={(e) => setForm({...form,details:e.target.value})} className="input-brut resize-none" data-testid="book-details" />
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="btn-primary flex-1 justify-center" data-testid="book-submit">{busy ? "Sending…" : "Send Request"}</button>
            <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          </div>
        </form>
      )}
    </ModalShell>
  );
}

function EditProfileModal({ profile, onClose, onSaved }) {
  const fileRef = useRef(null);
  const bannerRef = useRef(null);
  const [f, setF] = useState({
    display_name: profile.display_name || "",
    bio: profile.bio || "",
    description: profile.description || "",
    avatar: profile.avatar || "",
    banner: profile.banner || "",
    is_artist: !!profile.is_artist,
    account_type: profile.account_type || "fan",
    service_title: profile.service_title || "",
    company_name: profile.company_name || "",
    social_links: profile.social_links || {},
    website: profile.website || "",
    genre: profile.genre || "",
    location: profile.location || "",
    city: profile.city || "",
    state: profile.state || "",
    open_to_bookings: !!profile.open_to_bookings,
    availability: profile.availability || [],
    rate_amount: profile.rate_amount || 0,
    rate_unit: profile.rate_unit || "hour",
    skills: profile.skills || [],
    experience_level: profile.experience_level || "",
    is_indy: !!profile.is_indy,
  });
  const [skillInput, setSkillInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [err, setErr] = useState("");

  const handleAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, avatar: r.data.url }));
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const handleBanner = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/profile/banner", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, banner: r.data.url }));
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Banner upload failed");
    } finally { setUploadingBanner(false); }
  };

  const toggleAvail = (a) => setF((s) => ({
    ...s,
    availability: s.availability.includes(a) ? s.availability.filter((x) => x !== a) : [...s.availability, a],
  }));

  const addSkill = () => {
    const v = skillInput.trim();
    if (!v) return;
    if (f.skills.includes(v)) { setSkillInput(""); return; }
    setF((s) => ({ ...s, skills: [...s.skills, v] }));
    setSkillInput("");
  };
  const removeSkill = (s) => setF((st) => ({ ...st, skills: st.skills.filter((x) => x !== s) }));

  const setLink = (k, v) => setF((s) => ({ ...s, social_links: { ...s.social_links, [k]: v } }));

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const links = Object.fromEntries(Object.entries(f.social_links).filter(([, v]) => v && v.trim()));
      const r = await api.put("/profile", { ...f, social_links: links, rate_amount: parseFloat(f.rate_amount) || 0 });
      onSaved(r.data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell onClose={onClose} testid="edit-profile-modal">
      <div className="flex justify-between items-center mb-6">
        <div>
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-1">YOUR PROFILE</div>
          <h3 className="font-headline text-2xl uppercase">Edit Profile</h3>
        </div>
        <button onClick={onClose}><X size={18} /></button>
      </div>

      {/* Banner */}
      <div className="mb-5">
        <div className="font-mono-label text-[10px] text-neutral-400 mb-2">BANNER (1500×500 RECOMMENDED)</div>
        {f.banner ? (
          <img src={resolveFileUrl(f.banner)} alt="" className="w-full h-28 object-cover border border-neutral-800 mb-2" data-testid="edit-banner-preview" />
        ) : (
          <div className="w-full h-28 border border-dashed border-neutral-700 bg-neutral-950 flex items-center justify-center font-mono-label text-[10px] text-neutral-600 mb-2">NO BANNER UPLOADED</div>
        )}
        <button onClick={() => bannerRef.current?.click()} className="btn-ghost" data-testid="edit-banner-btn">
          <Upload size={14} /> {uploadingBanner ? "Uploading…" : f.banner ? "Change Banner" : "Upload Banner"}
        </button>
        <input ref={bannerRef} type="file" accept="image/*" onChange={handleBanner} className="hidden" />
        {f.banner && (
          <button onClick={() => setF((s) => ({ ...s, banner: "" }))} className="ml-2 font-mono-label text-[10px] text-neutral-400 hover:text-[var(--accent)]" data-testid="edit-banner-clear">REMOVE</button>
        )}
      </div>

      {/* Avatar */}
      <div className="flex items-center gap-4 mb-5">
        {f.avatar ? (
          <img src={resolveFileUrl(f.avatar)} alt="" className="w-20 h-20 object-cover border border-neutral-800" data-testid="edit-avatar-preview" />
        ) : (
          <div className="w-20 h-20 border border-neutral-800 bg-neutral-900 flex items-center justify-center font-display text-2xl text-neutral-500">
            {(f.display_name || "?").slice(0, 2).toUpperCase()}
          </div>
        )}
        <div>
          <button onClick={() => fileRef.current?.click()} className="btn-ghost" data-testid="edit-avatar-btn">
            <Upload size={14} /> {uploading ? "Uploading…" : f.avatar ? "Change Avatar" : "Upload Avatar"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatar} className="hidden" />
          {f.avatar && (
            <button onClick={() => setF((s) => ({ ...s, avatar: "" }))} className="ml-2 font-mono-label text-[10px] text-neutral-400 hover:text-[var(--accent)]" data-testid="edit-avatar-clear">REMOVE</button>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <input className="input-brut" placeholder="Display Name" value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} data-testid="edit-display-name" />
        <textarea rows={2} className="input-brut resize-none" placeholder="Short bio (max 500 chars)" maxLength={500} value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} data-testid="edit-bio" />
        <textarea rows={4} className="input-brut resize-none" placeholder="Long description / about (services, experience, what you're looking for)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="edit-description" />

        {/* Account type */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-2">
          {[
            { k: "creative", label: "Creative" },
            { k: "talent", label: "Talent" },
            { k: "education", label: "Education" },
            { k: "employer", label: "Employer" },
            { k: "fan", label: "Fan" },
          ].map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setF((s) => ({ ...s, account_type: t.k, is_artist: ["creative", "talent"].includes(t.k) ? true : s.is_artist }))}
              className={`font-mono-label text-[10px] py-2 border ${f.account_type === t.k ? "border-[var(--accent)] text-[var(--accent)]" : "border-neutral-800 text-neutral-400 hover:text-white"}`}
              data-testid={`edit-type-${t.k}`}
            >
              {t.label.toUpperCase()}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-3 font-mono-label text-[11px] text-neutral-300 cursor-pointer pt-1">
          <input type="checkbox" checked={f.is_artist} onChange={(e) => setF({ ...f, is_artist: e.target.checked })} data-testid="edit-is-artist" />
          ENABLE ARTIST FEATURES (TIPPING / DISCOGRAPHY / EARNINGS)
        </label>

        {/* Culture Connect details */}
        <div className="pt-3 space-y-3 border-t border-neutral-800">
          <div className="font-mono-label text-[10px] text-[var(--accent)]">CULTURE CONNECT DETAILS</div>
          {f.account_type === "employer" ? (
            <input className="input-brut" placeholder="Company name" value={f.company_name} onChange={(e) => setF({ ...f, company_name: e.target.value })} data-testid="edit-company-name" />
          ) : (
            <input className="input-brut" placeholder="Service title (e.g. Videographer, A&R, Mixing Engineer)" value={f.service_title} onChange={(e) => setF({ ...f, service_title: e.target.value })} data-testid="edit-service-title" />
          )}

          <div className="grid grid-cols-2 gap-3">
            <input className="input-brut" placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="edit-city" />
            <input className="input-brut" placeholder="State (e.g. CA)" maxLength={2} value={f.state} onChange={(e) => setF({ ...f, state: e.target.value.toUpperCase() })} data-testid="edit-state" />
          </div>

          {/* Rate */}
          <div className="grid grid-cols-[1fr_140px] gap-3">
            <input type="number" min="0" className="input-brut" placeholder="Rate (USD)" value={f.rate_amount} onChange={(e) => setF({ ...f, rate_amount: e.target.value })} data-testid="edit-rate-amount" />
            <select className="input-brut" value={f.rate_unit} onChange={(e) => setF({ ...f, rate_unit: e.target.value })} data-testid="edit-rate-unit">
              <option value="hour">per hour</option>
              <option value="day">per day</option>
              <option value="project">per project</option>
            </select>
          </div>

          {/* Availability */}
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">AVAILABILITY</div>
            <div className="flex flex-wrap gap-2">
              {["full_time", "part_time", "freelance", "contract"].map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => toggleAvail(a)}
                  className={`font-mono-label text-[10px] px-3 py-2 border ${f.availability.includes(a) ? "border-[var(--accent)] text-[var(--accent)]" : "border-neutral-800 text-neutral-400 hover:text-white"}`}
                  data-testid={`edit-avail-${a}`}
                >
                  {a.replace("_", " ").toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">EXPERIENCE LEVEL</div>
            <select className="input-brut" value={f.experience_level} onChange={(e) => setF({ ...f, experience_level: e.target.value })} data-testid="edit-experience">
              <option value="">— Pick one —</option>
              <option value="entry">Entry</option>
              <option value="mid">Mid-level</option>
              <option value="senior">Senior</option>
              <option value="expert">Expert</option>
            </select>
          </div>

          {/* Skills */}
          <div>
            <div className="font-mono-label text-[10px] text-neutral-400 mb-2">SKILLS / SERVICES</div>
            <div className="flex gap-2 mb-2">
              <input className="input-brut" placeholder="Add a skill and hit +" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }} data-testid="edit-skill-input" />
              <button type="button" onClick={addSkill} className="btn-ghost shrink-0" data-testid="edit-skill-add">+</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {f.skills.map((s) => (
                <span key={s} className="font-mono-label text-[10px] text-neutral-300 border border-neutral-800 px-2 py-1 inline-flex items-center gap-2" data-testid={`edit-skill-chip-${s}`}>
                  {s}
                  <button type="button" onClick={() => removeSkill(s)} className="text-neutral-500 hover:text-[var(--accent)]"><X size={10} /></button>
                </span>
              ))}
            </div>
          </div>

          {f.is_artist && (
            <>
              <input className="input-brut" placeholder="Primary Genre (e.g. Boom Bap, Drill, Lo-Fi)" value={f.genre} onChange={(e) => setF({ ...f, genre: e.target.value })} data-testid="edit-genre" />
              <input className="input-brut" placeholder="Location label (legacy — e.g. Brooklyn, NY)" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="edit-location" />
              <label className="flex items-center gap-3 font-mono-label text-[11px] text-neutral-300 cursor-pointer">
                <input type="checkbox" checked={f.open_to_bookings} onChange={(e) => setF({ ...f, open_to_bookings: e.target.checked })} data-testid="edit-open-to-bookings" />
                OPEN TO BOOKINGS (BADGE ON CULTURE CONNECT)
              </label>
              <label className="flex items-center gap-3 font-mono-label text-[11px] text-neutral-300 cursor-pointer">
                <input type="checkbox" checked={f.is_indy} onChange={(e) => setF({ ...f, is_indy: e.target.checked })} data-testid="edit-indy" />
                TAG PROFILE AS <span className="text-[var(--accent)]">INDY</span> — INDEPENDENT / UNSIGNED
              </label>
            </>
          )}
        </div>

        <div className="pt-3">
          <div className="font-mono-label text-[10px] text-neutral-400 mb-2">WEBSITE & SOCIAL LINKS</div>
          <SocialInput k="website" label="Website URL" value={f.social_links.website || ""} onChange={setLink} icon={Globe} />
          <SocialInput k="instagram" label="Instagram URL" value={f.social_links.instagram || ""} onChange={setLink} icon={Instagram} />
          <SocialInput k="twitter" label="Twitter / X URL" value={f.social_links.twitter || ""} onChange={setLink} icon={Twitter} />
          <SocialInput k="youtube" label="YouTube URL" value={f.social_links.youtube || ""} onChange={setLink} icon={Youtube} />
          <SocialInput k="spotify" label="Spotify URL" value={f.social_links.spotify || ""} onChange={setLink} icon={Music} />
        </div>

        {err && <p className="text-[var(--accent)] font-mono-label text-xs" data-testid="edit-error">{err}</p>}

        <div className="flex gap-2 pt-3 sticky bottom-0 bg-[var(--surface)] py-3 border-t border-neutral-800 -mx-6 px-6">
          <button onClick={save} disabled={busy} className="btn-primary flex-1 justify-center" data-testid="edit-save">
            {busy ? "Saving…" : "Save Profile"}
          </button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </div>
    </ModalShell>
  );
}

function SocialInput({ k, label, value, onChange, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon size={14} className="text-neutral-500 shrink-0" />
      <input className="input-brut" placeholder={label} value={value} onChange={(e) => onChange(k, e.target.value)} data-testid={`edit-social-${k}`} />
    </div>
  );
}

function UsersListModal({ title, endpoint, onClose, testid }) {
  const [users, setUsers] = useState(null);
  useEffect(() => { api.get(endpoint).then((r) => setUsers(r.data)); }, [endpoint]);
  return (
    <ModalShell onClose={onClose} testid={testid}>
      <div className="flex justify-between items-center mb-4">
        <div>
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-1">{title.toUpperCase()}</div>
          <h3 className="font-headline text-2xl uppercase">{title}</h3>
        </div>
        <button onClick={onClose}><X size={18} /></button>
      </div>
      {!users ? (
        <p className="font-mono-label text-[11px] text-neutral-500">Loading…</p>
      ) : users.length === 0 ? (
        <p className="font-mono-label text-[11px] text-neutral-500">No one here yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-800 max-h-[60vh] overflow-y-auto">
          {users.map((u) => (
            <li key={u.id}>
              <Link to={`/artist/${u.username}`} onClick={onClose} className="flex items-center gap-3 py-3 hover:bg-neutral-900 px-2" data-testid={`user-link-${u.username}`}>
                {u.avatar ? (
                  <img src={resolveFileUrl(u.avatar)} alt="" className="w-10 h-10 object-cover border border-neutral-800" />
                ) : (
                  <div className="w-10 h-10 border border-neutral-800 bg-neutral-900 flex items-center justify-center font-mono-label text-[10px]">{(u.display_name || u.username || "?").slice(0, 2).toUpperCase()}</div>
                )}
                <div className="min-w-0">
                  <div className="font-headline text-sm uppercase truncate">{u.display_name}</div>
                  <div className="font-mono-label text-[10px] text-neutral-500">@{u.username}{u.is_artist && " · ARTIST"}</div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function socialIcon(key) {
  const map = {
    instagram: <Instagram size={18} />,
    twitter: <Twitter size={18} />,
    youtube: <Youtube size={18} />,
    spotify: <Music size={18} />,
    website: <Globe size={18} />,
  };
  return map[key] || <Globe size={18} />;
}


// ─────────────────────────────────────────────────────────────────────────────
// PORTFOLIO
// ─────────────────────────────────────────────────────────────────────────────
function PortfolioSection({ profile, isSelf, onChange }) {
  const fileInput = React.useRef(null);
  const [busy, setBusy] = React.useState(false);
  const portfolio = profile.portfolio || [];

  const kindFromFile = (f) => {
    const ext = f.name.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png", "webp", "gif"].includes(ext)) return "image";
    if (["mp3", "wav", "m4a", "aac", "ogg", "flac"].includes(ext)) return "audio";
    if (["mp4", "mov", "webm", "mkv"].includes(ext)) return "video";
    if (ext === "pdf") return "pdf";
    if (ext === "zip") return "zip";
    return null;
  };

  const upload = async (f) => {
    const kind = kindFromFile(f);
    if (!kind) { toast.error("Unsupported file type. Use image / video / audio / pdf / zip."); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("kind", kind);
    fd.append("title", f.name);
    setBusy(true);
    try {
      await api.post("/portfolio", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove from portfolio?")) return;
    await api.delete(`/portfolio/${id}`);
    onChange?.();
  };

  return (
    <section className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12" data-testid="portfolio-section">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">PORTFOLIO</div>
          <h2 className="font-headline text-2xl md:text-3xl uppercase">Work Samples</h2>
        </div>
        {isSelf && (
          <>
            <input ref={fileInput} type="file" accept="image/*,video/*,audio/*,.pdf,.zip" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="portfolio-file-input" />
            <button onClick={() => fileInput.current?.click()} disabled={busy} className="btn-primary" data-testid="portfolio-upload-btn">
              <Upload size={14} /> {busy ? "Uploading…" : "Add Sample"}
            </button>
          </>
        )}
      </div>

      {portfolio.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-12 text-center">
          <ImageIcon size={28} className="mx-auto text-neutral-600 mb-4" />
          <p className="font-mono-label text-[11px] text-neutral-500">
            {isSelf ? "Add images, videos, audio, PDFs or ZIPs to showcase your work." : "Nothing posted yet."}
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="portfolio-grid">
          {portfolio.map((it) => (
            <div key={it.id} className="card-brut p-2 group relative" data-testid={`portfolio-item-${it.id}`}>
              <PortfolioMedia item={it} />
              <div className="p-2 flex items-center justify-between">
                <div className="font-mono-label text-[10px] text-neutral-300 truncate">{it.title}</div>
                {isSelf && (
                  <button onClick={() => remove(it.id)} className="text-neutral-500 hover:text-[var(--accent)] p-1" data-testid={`portfolio-delete-${it.id}`}>
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function PortfolioMedia({ item }) {
  const src = resolveFileUrl(item.url);
  if (item.kind === "image") return <img src={src} alt={item.title} className="w-full aspect-video object-cover" />;
  if (item.kind === "video") return <video src={src} controls className="w-full aspect-video object-cover bg-black" />;
  if (item.kind === "audio") return (
    <div className="w-full aspect-video bg-neutral-950 border border-neutral-800 flex flex-col items-center justify-center p-3">
      <Music size={24} className="text-[var(--accent)] mb-2" />
      <audio src={src} controls className="w-full" />
    </div>
  );
  return (
    <a href={src} target="_blank" rel="noreferrer" className="w-full aspect-video bg-neutral-950 border border-neutral-800 flex items-center justify-center hover:border-[var(--accent)]">
      <FileText size={28} className="text-[var(--accent)]" />
      <span className="font-mono-label text-[10px] text-neutral-400 ml-2">OPEN {item.kind?.toUpperCase()}</span>
    </a>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REVIEWS
// ─────────────────────────────────────────────────────────────────────────────
function ReviewsSection({ profile }) {
  const reviews = profile.reviews || [];
  return (
    <section className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12" data-testid="reviews-section">
      <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">REVIEWS</div>
      <h2 className="font-headline text-2xl md:text-3xl uppercase mb-6">
        {profile.rating_count > 0
          ? <>{profile.rating_avg.toFixed(1)} ★ · {profile.rating_count} reviews</>
          : "Reviews"}
      </h2>
      {reviews.length === 0 ? (
        <div className="border border-dashed border-neutral-800 p-10 text-center">
          <Star size={24} className="mx-auto text-neutral-600 mb-3" />
          <p className="font-mono-label text-[11px] text-neutral-500">No reviews yet. Complete a booking to start collecting reviews.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="reviews-list">
          {reviews.map((r) => (
            <div key={r.id} className="card-brut p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-3 mb-2">
                {r.reviewer_avatar && <img src={resolveFileUrl(r.reviewer_avatar)} alt="" className="w-8 h-8 object-cover border border-neutral-800" />}
                <div className="flex-1 min-w-0">
                  <div className="font-headline uppercase text-sm truncate">{r.reviewer_name}</div>
                  <div className="font-mono-label text-[9px] text-neutral-500">{new Date(r.created_at).toLocaleDateString()}</div>
                </div>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={12} className={n <= r.rating ? "text-[var(--accent)] fill-[var(--accent)]" : "text-neutral-700"} />
                  ))}
                </div>
              </div>
              {r.comment && <p className="text-neutral-300 text-sm">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
